import React, { useState, useEffect } from 'react';
import { subscribeToDeadlineAlerts } from '../services/api';
import './DeadlineAlerts.css';

function DeadlineAlerts() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  
  // Custom captcha state
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaChallenge, setCaptchaChallenge] = useState({ num1: 0, num2: 0, expected: 0 });

  // Generate a new captcha challenge
  const generateCaptchaChallenge = () => {
    const num1 = Math.floor(Math.random() * 10) + 1; // Random number between 1-10
    const num2 = Math.floor(Math.random() * 10) + 1; // Random number between 1-10
    const expected = num1 + num2;
    setCaptchaChallenge({ num1, num2, expected });
    setCaptchaAnswer('');
    setCaptchaVerified(false);
  };

  // Initialize captcha on component mount
  useEffect(() => {
    generateCaptchaChallenge();
  }, []);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    // Reset states when user starts typing again
    if (success) setSuccess(false);
    if (error) setError('');
  };

  const handleCaptchaChange = (e) => {
    const value = e.target.value;
    setCaptchaAnswer(value);
    
    // Verify captcha answer
    if (parseInt(value, 10) === captchaChallenge.expected) {
      setCaptchaVerified(true);
    } else {
      setCaptchaVerified(false);
    }
  };

  // Email validation function
  const validateEmail = (email) => {
    const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(String(email).toLowerCase());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Email validation
    if (!email) {
      setError('Please enter your business email');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Captcha validation
    if (!captchaVerified) {
      setError('Please solve the math problem correctly');
      return;
    }

    try {
      setLoading(true);
      await subscribeToDeadlineAlerts({
        email,
        ip_address: null // This will be captured by the server
      });
      
      setSuccess(true);
      setEmail('');
      // Generate a new captcha challenge after successful submission
      generateCaptchaChallenge();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to subscribe. Please try again.');
      // Generate a new captcha challenge after failure
      generateCaptchaChallenge();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="deadline-alerts-page">
      {/* Hero Section */}
      <section className="deadline-hero-section">
        <div className="container">
          <h1>Stay on top of every deadline</h1>
          <p className="deadline-subtitle">
            Get timely reminders for a curated list of tax deadlines designed to keep you well-prepared and organized for the tax season ahead.
          </p>
          {/* <p className="deadline-corp-text">For U.S. Delaware C-Corps.</p> */}

          <div className="deadline-form-container">
            <form onSubmit={handleSubmit} className="deadline-form">
              <div className="input-group">
                <input
                  type="email"
                  placeholder="Enter your Business Email"
                  value={email}
                  onChange={handleEmailChange}
                  className="email-input"
                />
                <button 
                  type="submit" 
                  className="get-reminders-btn"
                  disabled={loading || !captchaVerified}
                >
                  {loading ? 'Processing...' : 'Get Reminders'}
                </button>
              </div>
              
              {error && <div className="error-message">{error}</div>}
              
              <div className="captcha-container">
                <div className="captcha-challenge">
                  <label htmlFor="captcha-answer">Verify you're human: What is {captchaChallenge.num1} + {captchaChallenge.num2}?</label>
                  <div className="captcha-input-group">
                    <input
                      id="captcha-answer"
                      type="text"
                      value={captchaAnswer}
                      onChange={handleCaptchaChange}
                      className="captcha-input"
                      placeholder="Enter the answer"
                    />
                    {captchaVerified && (
                      <span className="captcha-verified">✓</span>
                    )}
                  </div>
                  <button 
                    type="button" 
                    className="refresh-captcha-btn"
                    onClick={generateCaptchaChallenge}
                  >
                    ↻ New Question
                  </button>
                </div>
              </div>
              
              {success && (
                <div className="success-message">
                  Thanks for subscribing! You'll receive deadline reminders soon.
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Illustration Section */}
      <section className="deadline-illustration-section">
        <div className="container">
          <div className="illustration-container">
            <img 
              src="/images/deadline-alerts.png" 
              alt="Tax deadline reminders illustration" 
              className="deadline-illustration"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      {/* <section className="deadline-features-section">
        <div className="container">
          <h2>A founder-centric, all-encompassing calendar</h2>
          <p className="feature-subtitle">
            Keep track of all the different tax deadlines that apply to you - U.S. State and Federal.
          </p>

          <div className="features-grid">
            <div className="feature-box">
              <h3>Be informed in real-time</h3>
              <p>
                Stay updated instantly through our real-time reminder feature.
              </p>
              
              <div className="feature-image-container">
                <img 
                  src="/images/real-time-notifications.png" 
                  alt="Real-time notifications" 
                  className="feature-image" 
                />
              </div>
            </div>

            <div className="feature-box">
              <h3>Your tax deadline calendar</h3>
              <p>
                We curate your tax calendar for U.S. State, Federal tax, and compliance deadlines.
              </p>
              
              <div className="feature-image-container">
                <img 
                  src="/images/tax-calendar.png" 
                  alt="Tax deadline calendar" 
                  className="feature-image" 
                />
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="deadline-cta-section">
        <div className="container">
          <h2>Ready to stay on top of your tax deadlines?</h2>
          <p>Join hundreds of startups using our deadline reminders to stay compliant.</p>
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="scroll-to-form-btn"
          >
            Get Deadline Reminders
          </button>
        </div>
      </section>
    </div>
  );
}

export default DeadlineAlerts; 